import React, { createContext, FC, ReactNode, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import { HelpArticleContent, HelpArticleLoadingFallback } from 'src/components/pages/products/productSidebar/sections/helpArticlePanel';
import { Link } from '../buttons';
import { SupportTicketDropdownTrigger } from '../supportTicketDropdown';

export const SIDEBAR_COLLAPSE_EVENT = 'zakeke:request-sidebar-collapse';

const HELP_PANEL_WIDTH = 520;

const Wrapper = styled.div<{ $panelOpen: boolean; $fillAvailableSpace: boolean }>`
    display: flex;
    ${(props) => (props.$panelOpen || props.$fillAvailableSpace) && 'flex: 1;'}
    margin: ${(props) => (props.$panelOpen ? '0 -48px 0 0' : '0')};
    transition: margin 0.3s ease;
`;

const Content = styled.div<{ $panelOpen: boolean }>`
    flex: 1;
    min-width: 0;
    padding-right: ${(props) => (props.$panelOpen ? '16px' : '0')};
    transition: padding-right 0.3s ease;
`;

const Divider = styled.div<{ $open: boolean }>`
    width: ${(props) => (props.$open ? '1px' : '0')};
    background: #4b5563;
    flex-shrink: 0;
    align-self: stretch;
    transition: width 0.3s ease;
`;

const Panel = styled.div<{ $open: boolean; $width: number }>`
    width: ${(props) => (props.$open ? props.$width : 0)}px;
    flex-shrink: 0;
    overflow: hidden;
    transition: width 0.3s ease;
    background: white;
    display: flex;
    flex-direction: column;
    height: 100vh;
    position: sticky;
    top: 0;
`;

const PanelInner = styled.div<{ $width: number }>`
    width: ${(props) => props.$width}px;
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const PanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
    gap: 8px;
`;

const PanelTitle = styled.h3`
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const PanelActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const PanelBody = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
`;

const PanelFooter = styled.div`
    flex-shrink: 0;
    border-top: 1px solid #e5e7eb;
    padding: 12px 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 13px;
    color: #6b7280;
`;

const LeaveMessageLink = styled(Link)`
    padding: 0;
    font-family: inherit;
    font-size: 13px;
`;

const ActionButton = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: white;
    cursor: pointer;
    color: #6b7280;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 18px;

    &:hover {
        background: #f3f4f6;
        color: #111827;
    }
`;

const ExternalLink = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: white;
    color: #6b7280;
    text-decoration: none;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
        background: #f3f4f6;
        color: #111827;
    }

    svg {
        width: 16px;
        height: 16px;
    }
`;

function extractArticleId(url: string): string {
    const match = url.match(/\/articles\/(\d+)/);
    return match ? match[1] : '';
}

interface InlineHelpContextValue {
    openHelpGuide: (url: string) => void;
}

const InlineHelpContext = createContext<InlineHelpContextValue | null>(null);

export const useInlineHelp = () => {
    const context = useContext(InlineHelpContext);

    if (!context) {
        throw new Error('useInlineHelp must be used within an InlineHelpLayout.');
    }

    return context;
};

export interface InlinePanelLayoutProps {
    children: ReactNode;
    isOpen: boolean;
    onClose: () => void;
    panelTitle: ReactNode;
    panelActions?: ReactNode;
    panelContent: ReactNode;
    panelWidth?: number;
    panelFooter?: ReactNode;
    externalUrl?: string;
}

export const InlinePanelLayout: FC<InlinePanelLayoutProps> = ({
    children,
    isOpen,
    onClose,
    panelTitle,
    panelActions,
    panelContent,
    panelWidth = HELP_PANEL_WIDTH,
    panelFooter,
    externalUrl
}) => {
    const wasOpenRef = useRef(false);

    useEffect(() => {
        if (isOpen && !wasOpenRef.current) {
            window.dispatchEvent(new CustomEvent(SIDEBAR_COLLAPSE_EVENT, { detail: { collapsed: true } }));
        } else if (!isOpen && wasOpenRef.current) {
            window.dispatchEvent(new CustomEvent(SIDEBAR_COLLAPSE_EVENT, { detail: { collapsed: false } }));
        }
        wasOpenRef.current = isOpen;

        // Communicate panel width to fixed-position elements (e.g. page footer)
        document.documentElement.style.setProperty('--help-panel-width', isOpen ? `${panelWidth + 1}px` : '0px');
    }, [isOpen, panelWidth]);

    useEffect(() => {
        return () => {
            if (wasOpenRef.current) {
                window.dispatchEvent(new CustomEvent(SIDEBAR_COLLAPSE_EVENT, { detail: { collapsed: false } }));
            }
            document.documentElement.style.setProperty('--help-panel-width', '0px');
        };
    }, []);

    return (
        <Wrapper $panelOpen={isOpen} $fillAvailableSpace>
            <Content $panelOpen={isOpen}>{children}</Content>
            <Divider $open={isOpen} />
            <Panel $open={isOpen} $width={panelWidth}>
                <PanelInner $width={panelWidth}>
                    <PanelHeader>
                        <PanelTitle>{panelTitle}</PanelTitle>
                        <PanelActions>
                            {panelActions}
                            {externalUrl && (
                                <ExternalLink href={externalUrl} target='_blank' rel='noopener noreferrer' title={T._('Open in new tab', 'Admin')}>
                                    <svg viewBox='0 0 20 20' fill='currentColor'>
                                        <path
                                            fillRule='evenodd'
                                            d='M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5zm7.25-.75a.75.75 0 01.75-.75h3.5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0V6.31l-5.47 5.47a.75.75 0 01-1.06-1.06l5.47-5.47H12.25a.75.75 0 01-.75-.75z'
                                            clipRule='evenodd'
                                        />
                                    </svg>
                                </ExternalLink>
                            )}
                            <ActionButton onClick={onClose} title={T._('Close', 'Admin')}>
                                &times;
                            </ActionButton>
                        </PanelActions>
                    </PanelHeader>
                    <PanelBody>{panelContent}</PanelBody>
                    {panelFooter}
                </PanelInner>
            </Panel>
        </Wrapper>
    );
};

export interface InlineHelpLayoutProps {
    children: ReactNode;
}

export const InlineHelpLayout: FC<InlineHelpLayoutProps> = ({ children }) => {
    const [showPanel, setShowPanel] = useState(false);
    const [helpTitle, setHelpTitle] = useState(T._('Guide', 'Admin'));
    const [activeUrl, setActiveUrl] = useState('');
    const articleId = extractArticleId(activeUrl);
    const openHelpGuide = useCallback((url: string) => {
        setActiveUrl(url);
        setHelpTitle(T._('Guide', 'Admin'));
        setShowPanel(true);
    }, []);
    const contextValue = useMemo(() => ({ openHelpGuide }), [openHelpGuide]);

    return (
        <InlineHelpContext.Provider value={contextValue}>
            <InlinePanelLayout
                isOpen={showPanel}
                onClose={() => setShowPanel(false)}
                panelTitle={helpTitle}
                externalUrl={activeUrl}
                panelContent={
                    showPanel && articleId ? (
                        <Suspense fallback={<HelpArticleLoadingFallback />}>
                            <HelpArticleContent articleId={articleId} onTitleLoaded={setHelpTitle} />
                        </Suspense>
                    ) : null
                }
                panelFooter={
                    <PanelFooter>
                        <span>{T._('Still need help?', 'Admin')}</span>
                        <SupportTicketDropdownTrigger placement='top-start'>
                            {({ onClick, isOpen }) => (
                                <LeaveMessageLink as='button' type='button' aria-expanded={isOpen} onClick={onClick}>
                                    {T._('Leave us a message', 'Admin')}
                                </LeaveMessageLink>
                            )}
                        </SupportTicketDropdownTrigger>
                    </PanelFooter>
                }
            >
                {children}
            </InlinePanelLayout>
        </InlineHelpContext.Provider>
    );
};
